/**
 * B2B Netflix Academy & RFQ Engine — API-client (opdrachtgevers-cockpit + opleider).
 * Backend: src/routes/b2bAcademy.ts (/api/b2b-academy)
 */
import api from './api';

export type B2bRij = 'mentaal' | 'fysiek' | 'vakinhoudelijk' | 'passie';
export type B2bInkoopType = 'incompany' | 'bulk_seats' | 'licentie';
export type B2bFiscaal = 'gericht_vrijgesteld' | 'studie_opleiding' | 'wkr';
export type RfqStatus = 'RFQ_SUBMITTED' | 'OFFER_SENT' | 'OFFER_ACCEPTED' | 'OFFER_DECLINED';
/** Opleider-uitbetaling van een geaccepteerde RFQ (migratie 156). */
export type RfqPayoutStatus = 'pending' | 'wacht_op_factuur' | 'completed' | 'failed' | 'skipped';
/** Werkgeversstap op de uitvoerdatum (besluit 5, migratie 197). */
export type UitvoerdatumStatus = 'wacht_op_werkgever' | 'bevestigd' | 'betwist';
export type RfqLocatie = 'eigen_locatie' | 'extern' | 'online';
/** Logistieke Locatiematrix (opleider-scope) — zelfde keys als de RFQ-locatievoorkeur. */
export type B2bLeverlocatie = RfqLocatie;
export type B2bFiscaalFilter = 'vrijgesteld' | 'wkr';
/** Vrij te kiezen groepsgrootte: aantal deelnemers (1 – 10.000). */
export type B2bGroepsgrootte = number;
export type B2bSortering = 'relevantie' | 'populair' | 'roi' | 'impact' | 'prijs';
export type RfqPlanning = 'binnen_1_maand' | 'binnen_3_maanden' | 'overleg';

export interface B2bRijInfo {
  key: B2bRij;
  rij: number;
  label: string;
  badge: string;
  emoji: string;
  kleur: string;
  fiscaal: B2bFiscaal;
  fiscaal_label: string;
  aanbod: string;
  doel: string;
}

export interface B2bTraining {
  id: number;
  titel: string;
  soort_opleiding: string | null;
  leervorm: 'e_learning' | 'workshop' | 'opleiding';
  duur: string | null;
  samenvatting: string | null;
  omslagfoto_url: string | null;
  locaties: string | null;
  locatie_detail: string | null;
  organisatie_naam: string;
  provider_logo: string | null;
  provider_id: number;
  rij: B2bRij;
  rij_label: string;
  rij_emoji: string;
  rij_kleur: string;
  fiscaal: B2bFiscaal;
  fiscaal_label: string;
  wkr_belast: boolean;
  inkoop_type: B2bInkoopType;
  employer_category: string | null;
  vanaf_prijs: number;
  vanaf_korting: number;
  vanaf_netto: number;
  short_pitch: string | null;
  populair: boolean;
  /** Meest Populair (Organisatie-breed): top 10% meest ingekochte B2B-trajecten. */
  top_populair: boolean;
  inkoop_score: number;
  leverlocaties: B2bLeverlocatie[];
  min_groep: number | null;
  max_groep: number | null;
  certificaat: boolean;
  roi_rating: { gemiddelde: number; aantal: number } | null;
  /** Hoogste Impact: gemiddelde 'toepasbaarheid' van werkgeversreviews (≥ drempel). */
  impact_score: number | null;
}

export interface B2bTrainingDetail extends B2bTraining {
  content: {
    title: string;
    short_pitch: string | null;
    target_audience: string | null;
    organizational_goal: string | null;
    program_description: string | null;
    duration_logistics: string | null;
    leerdoelen: string | null;
    docenten: string | null;
    accreditatie: string | null;
  };
  provider_website: string | null;
  provider_bio: string | null;
  reviews: { aantal: number; balken: { relevantie: number; toepasbaarheid: number; prijs_kwaliteit: number } | null };
}

export interface B2bRijMetTrainingen extends B2bRijInfo {
  trainingen: B2bTraining[];
}

export interface Rfq {
  id: string;
  training_id: number;
  provider_id: number;
  employer_id: string;
  titel: string | null;
  organisatie_naam: string | null;
  bedrijfsnaam: string | null;
  omslagfoto_url: string | null;
  rij: B2bRij | null;
  rij_label: string | null;
  wkr_belast: boolean;
  afdeling: string | null;
  deelnemers: number;
  locatie_voorkeur: RfqLocatie;
  planning: RfqPlanning;
  notities: string | null;
  status: RfqStatus;
  marktprijs: number | null;
  korting_bedrag: number | null;
  offertewaarde: number | null;
  platformfee: number | null;
  netto_opleider: number | null;
  offerte_toelichting: string | null;
  offerte_verzonden_at: string | null;
  beslist_at: string | null;
  factuur_opdracht_nummer: string | null;
  ingeroosterd: boolean;
  /** Laatste trainingsdag (YYYY-MM-DD); de Academy-verzamelfactuur van de 1e daarna factureert de RFQ. */
  uitvoerdatum: string | null;
  /** Alleen 'bevestigd' komt op de verzamelfactuur; null = nog geen datum. */
  uitvoerdatum_status: UitvoerdatumStatus | null;
  uitvoerdatum_bevestigd_at: string | null;
  uitvoerdatum_betwist_at: string | null;
  uitvoerdatum_betwist_reden: string | null;
  gefactureerd: boolean;
  opleider_payout_status: RfqPayoutStatus;
  created_at: string;
}

export interface WkrRuimte {
  resterend: number | null;
  op: boolean;
  bron: 'payroll_bridge' | 'hobby_cap' | 'geen';
}

export interface B2bAcademyOverzicht {
  rijen_info: B2bRijInfo[];
  /** Rij 1 "🔥 Populair op uw kostenplaats" (Aanbevolen). */
  aanbevolen: B2bTraining[];
  rijen: B2bRijMetTrainingen[];
  rfqs: Rfq[];
  korting_pct: number;
  platformfee_pct: number;
  wkr: WkrRuimte;
}

export interface B2bZoekParams {
  q?: string | null;
  rij?: B2bRij | null;
  vorm?: B2bInkoopType | null;
  fiscaal?: B2bFiscaalFilter[];
  locatie?: B2bLeverlocatie[];
  groep?: B2bGroepsgrootte | null;
  sort?: B2bSortering;
  alles?: boolean;
}

export interface B2bZoekResultaat {
  trainingen: Array<B2bTraining & { relevantie: number | null }>;
  meta: {
    aantal: number;
    fuzzy: boolean;
    synoniem_rijen: B2bRij[];
    wkr: WkrRuimte;
    wkr_grendel_actief: boolean;
    sort: B2bSortering;
  };
}

export interface B2bSuggesties {
  trajecten: Array<{ id: number; titel: string; rij: B2bRij; rij_emoji: string; inkoop_type: B2bInkoopType; organisatie_naam: string }>;
  opleiders: Array<{ id: number; naam: string; logo_url: string | null; aantal: number }>;
  rijen: Array<{ key: B2bRij; label: string; emoji: string; term: string }>;
}

export interface NieuweRfq {
  afdeling?: string | null;
  deelnemers: number;
  locatie_voorkeur: RfqLocatie;
  planning: RfqPlanning;
  notities?: string | null;
}

const d = <T>(r: { data: { data: T } }) => r.data.data;

export const getB2bAcademy = () => api.get<{ data: B2bAcademyOverzicht }>('/b2b-academy').then(d);
export const getB2bTrainingen = (filter: { rij?: string | null; zoek?: string | null }) =>
  api.get<{ data: B2bTraining[] }>('/b2b-academy/trainingen', { params: { rij: filter.rij || undefined, zoek: filter.zoek || undefined } }).then(d);
export const getB2bZoek = (p: B2bZoekParams) =>
  api
    .get<{ data: B2bZoekResultaat }>('/b2b-academy/zoek', {
      params: {
        q: p.q || undefined,
        rij: p.rij || undefined,
        vorm: p.vorm || undefined,
        fiscaal: p.fiscaal?.length ? p.fiscaal.join(',') : undefined,
        locatie: p.locatie?.length ? p.locatie.join(',') : undefined,
        groep: p.groep ? String(p.groep) : undefined,
        sort: p.sort && p.sort !== 'relevantie' ? p.sort : undefined,
        alles: p.alles ? '1' : undefined,
      },
    })
    .then(d);
export const getB2bSuggesties = (q: string, signal?: AbortSignal) =>
  api.get<{ data: B2bSuggesties }>('/b2b-academy/suggesties', { params: { q }, signal }).then(d);
export const getB2bTraining = (id: number) => api.get<{ data: B2bTrainingDetail }>(`/b2b-academy/trainingen/${id}`).then(d);
export const dienRfqIn = (trainingId: number, invoer: NieuweRfq) => api.post<{ data: Rfq }>(`/b2b-academy/trainingen/${trainingId}/rfq`, invoer).then(d);
export const accepteerOfferte = (rfqId: string) => api.post<{ data: Rfq }>(`/b2b-academy/rfqs/${rfqId}/accepteer`).then(d);
export const weigerOfferte = (rfqId: string) => api.post<{ data: Rfq }>(`/b2b-academy/rfqs/${rfqId}/weiger`).then(d);
export const bevestigUitvoerdatum = (rfqId: string) => api.post<{ data: Rfq }>(`/b2b-academy/rfqs/${rfqId}/uitvoerdatum/bevestig`).then(d);
export const betwistUitvoerdatum = (rfqId: string, reden: string) =>
  api.post<{ data: Rfq }>(`/b2b-academy/rfqs/${rfqId}/uitvoerdatum/betwist`, { reden }).then(d);

// Opleider
export const getOpleiderRfqs = () => api.get<{ data: Rfq[] }>('/b2b-academy/opleider/rfqs').then(d);
export const verstuurOfferte = (rfqId: string, invoer: { marktprijs: number; toelichting?: string | null }) =>
  api.post<{ data: Rfq }>(`/b2b-academy/opleider/rfqs/${rfqId}/offerte`, invoer).then(d);
export const legUitvoerdatumVast = (rfqId: string, uitvoerdatum: string) =>
  api.post<{ data: Rfq }>(`/b2b-academy/opleider/rfqs/${rfqId}/uitvoerdatum`, { uitvoerdatum }).then(d);
